import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck, Loader2, PlayCircle, Plus, Mail } from "lucide-react";

const ROLES = ["telehealth", "nurse", "pilot", "attorney", "affiliate", "medical_provider",
  "physical_therapist", "chiropractor", "orthopedic", "neurologist", "pain_management", "private_investigator"];
const STATUSES = ["awaiting_review", "verified", "rejected", "expired", "waived", "extended"];
const TONE = {
  verified: "bg-emerald-50 text-emerald-600", extended: "bg-indigo-50 text-indigo-600",
  awaiting_review: "bg-amber-50 text-amber-600", waived: "bg-sky-50 text-sky-600",
  rejected: "bg-red-50 text-red-600", expired: "bg-red-50 text-red-600", missing: "bg-slate-100 text-slate-500",
};
const inputCls = "rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30";

const StatCard = ({ label, value, tone = "text-navy", testid }) => (
  <div className="bg-white rounded-2xl border border-slate-100 px-4 py-3" data-testid={testid}>
    <div className={`font-heading text-2xl font-extrabold ${tone}`}>{value}</div>
    <div className="text-[11px] text-slate-500">{label}</div>
  </div>
);

const Overview = () => {
  const [d, setD] = useState(null);
  const [digest, setDigest] = useState(null);
  const [running, setRunning] = useState(false);
  const [sending, setSending] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/credentials/dashboard").then(({ data }) => setD(data)).catch(() => {});
    api.get("/admin/credentials/digest").then(({ data }) => setDigest(data.last)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  return (
    <div data-testid="cred-admin-overview">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <p className="text-sm text-slate-500">Platform-wide credential health across every professional role.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={async () => {
            setSending(true);
            try { const { data } = await api.post("/admin/credentials/send-digest"); toast.success(`Digest sent to ${data.recipients.length} super admin(s)`); load(); }
            catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Digest failed"); }
            finally { setSending(false); }
          }} disabled={sending} data-testid="cred-send-digest" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cobalt text-white text-xs font-semibold disabled:opacity-50">
            <Mail className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send compliance digest"}
          </button>
          <button onClick={async () => {
          setRunning(true);
          try { const { data } = await api.post("/admin/credentials/run-sweep"); toast.success(`Sweep done — ${data.expired} expired, ${data.reminders_sent} reminders sent`); load(); }
          catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sweep failed"); }
          finally { setRunning(false); }
        }} disabled={running} data-testid="cred-run-sweep" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-navy text-white text-xs font-semibold disabled:opacity-50">
          <PlayCircle className="w-3.5 h-3.5" /> {running ? "Running…" : "Run expiry + reminder sweep"}
        </button>
        </div>
      </div>
      {digest && (
        <p className="text-[11px] text-slate-400 mb-3" data-testid="cred-digest-status">
          Last digest: week {digest.week} · {new Date(digest.at).toLocaleString()} · {digest.recipients?.length || 0} recipient(s)
          {digest.counts ? ` · ${digest.counts.expired} expired, ${digest.counts.expiring_30d} expiring ≤30d, ${digest.counts.awaiting_review} awaiting review` : ""}
          {digest.manual ? " · manual" : " · automatic weekly"}
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {Object.entries(d.expiring || {}).map(([k, v]) => (
          <StatCard key={k} label={`Expiring ≤ ${k.replace("d", " days")}`} value={v} tone={v > 0 ? "text-amber-600" : "text-navy"} testid={`cred-stat-expiring-${k}`} />
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        <StatCard label="Expired" value={d.expired} tone={d.expired > 0 ? "text-red-500" : "text-navy"} testid="cred-stat-expired" />
        <StatCard label="Missing (required)" value={d.missing} tone={d.missing > 0 ? "text-red-500" : "text-navy"} testid="cred-stat-missing" />
        <StatCard label="Awaiting review" value={d.awaiting_review} tone={d.awaiting_review > 0 ? "text-amber-600" : "text-navy"} testid="cred-stat-awaiting" />
        <StatCard label="Suspended providers" value={d.suspended_providers} testid="cred-stat-suspended" />
        <StatCard label="Temporary extensions" value={d.temporary_extensions} testid="cred-stat-extensions" />
        <StatCard label="Waived" value={d.waived} testid="cred-stat-waived" />
        <StatCard label="Failed reminder deliveries" value={d.failed_reminder_deliveries} tone={d.failed_reminder_deliveries > 0 ? "text-red-500" : "text-navy"} testid="cred-stat-failed" />
        <StatCard label="Total credentials" value={d.total_credentials} testid="cred-stat-total" />
      </div>
    </div>
  );
};

const CredentialList = () => {
  const [rows, setRows] = useState(null);
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const load = useCallback(() => {
    const q = new URLSearchParams(); if (role) q.set("role", role); if (status) q.set("status", status);
    api.get(`/admin/credentials?${q}`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [role, status]);
  useEffect(() => { load(); }, [load]);

  const act = async (c, action) => {
    let reason = "", until = null;
    if (["reject", "waive", "extend", "expire"].includes(action)) {
      reason = window.prompt("Administrative reason (required, permanently logged):");
      if (!reason) return;
    }
    if (action === "extend") {
      until = window.prompt("Extend until (YYYY-MM-DD):");
      if (!until) return;
    }
    try {
      await api.post(`/admin/credentials/${c.credential_id}/action`, { action, reason, until });
      toast.success(`Credential ${action}${action.endsWith("e") ? "d" : "ed"}`); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); }
  };

  return (
    <div data-testid="cred-admin-list">
      <div className="flex gap-2 mb-4 flex-wrap">
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="cred-filter-role" className={inputCls}>
          <option value="">All roles</option>{ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="cred-filter-status" className={inputCls}>
          <option value="">All statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </div>
      {rows === null ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div> :
        rows.length === 0 ? <div className="py-10 text-center text-sm text-slate-400">No credentials match these filters.</div> : (
          <div className="bg-white rounded-3xl border border-slate-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b border-slate-100">
                <th className="px-4 py-2.5">Holder</th><th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5">Credential</th>
                <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Expires</th><th className="px-4 py-2.5">Actions</th></tr></thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.credential_id} className="border-b border-slate-50 last:border-0" data-testid={`cred-row-${c.credential_id}`}>
                    <td className="px-4 py-2.5"><div className="font-medium text-navy">{c.holder?.name || "—"}</div><div className="text-[11px] text-slate-400">{c.holder?.email}</div></td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{(c.role || "").replace(/_/g, " ")}</td>
                    <td className="px-4 py-2.5"><div className="text-navy">{c.label}</div>{c.file?.filename && <div className="text-[10px] text-slate-400">{c.file.filename}</div>}</td>
                    <td className="px-4 py-2.5"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${TONE[c.status] || TONE.missing}`}>{c.status.replace(/_/g, " ")}</span>
                      {c.extension?.until && <div className="text-[10px] text-indigo-500 mt-0.5">ext → {c.extension.until}</div>}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{c.expires_at || "—"}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {c.status === "awaiting_review" && <>
                          <button onClick={() => act(c, "verify")} data-testid={`cred-verify-${c.credential_id}`} className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold">Verify</button>
                          <button onClick={() => act(c, "reject")} data-testid={`cred-reject-${c.credential_id}`} className="px-2.5 py-1 rounded-full bg-red-50 text-red-600 text-[10px] font-bold">Reject</button>
                        </>}
                        {["verified", "expired", "extended"].includes(c.status) && <button onClick={() => act(c, "extend")} data-testid={`cred-extend-${c.credential_id}`} className="px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600 text-[10px] font-bold">Extend</button>}
                        {c.status !== "waived" && <button onClick={() => act(c, "waive")} data-testid={`cred-waive-${c.credential_id}`} className="px-2.5 py-1 rounded-full bg-sky-50 text-sky-600 text-[10px] font-bold">Waive</button>}
                        {["verified", "extended", "waived"].includes(c.status) && <button onClick={() => act(c, "expire")} data-testid={`cred-expire-${c.credential_id}`} className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">Expire</button>}
                        {["expired", "rejected"].includes(c.status) && <button onClick={() => act(c, "reactivate")} data-testid={`cred-reactivate-${c.credential_id}`} className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold">Reactivate</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
};

const Templates = () => {
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ role: "medical_provider", credential_type: "", label: "", required: true, expires: true, blocks: "" });
  const load = useCallback(() => api.get("/admin/credentials/templates").then(({ data }) => setRows(data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const save = async (body) => {
    try { await api.put("/admin/credentials/templates", body); toast.success("Template saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
  };
  if (rows === null) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  return (
    <div data-testid="cred-admin-templates">
      <div className="bg-white rounded-3xl border border-slate-100 p-5 mb-4">
        <h4 className="font-heading font-semibold text-navy text-sm mb-3 flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add credential requirement</h4>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
          <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="cred-tpl-role" className={inputCls}>
            {ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select>
          <input value={f.credential_type} onChange={(e) => setF({ ...f, credential_type: e.target.value.toLowerCase().replace(/\s+/g, "_") })} data-testid="cred-tpl-type" className={inputCls} placeholder="type_key" />
          <input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="cred-tpl-label" className={inputCls} placeholder="Display label" />
          <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={f.required} onChange={(e) => setF({ ...f, required: e.target.checked })} className="accent-cobalt" /> Required</label>
          <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={f.expires} onChange={(e) => setF({ ...f, expires: e.target.checked })} className="accent-cobalt" /> Expires</label>
          <button onClick={() => {
            if (!f.credential_type || !f.label) { toast.error("Type key and label are required"); return; }
            save({ ...f, blocks: f.blocks || null, renewal_required: f.expires });
            setF({ role: f.role, credential_type: "", label: "", required: true, expires: true, blocks: "" });
          }} data-testid="cred-tpl-add" className="px-4 py-2 rounded-full bg-navy text-white text-xs font-semibold">Add</button>
        </div>
      </div>
      <div className="bg-white rounded-3xl border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b border-slate-100">
            <th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5">Credential</th><th className="px-4 py-2.5">Required</th>
            <th className="px-4 py-2.5">Expires</th><th className="px-4 py-2.5">Blocks when expired</th><th className="px-4 py-2.5">Reminders</th><th className="px-4 py-2.5">Active</th></tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.template_id} className="border-b border-slate-50 last:border-0" data-testid={`cred-tpl-${t.template_id}`}>
                <td className="px-4 py-2.5 text-xs text-slate-500">{(t.role || "").replace(/_/g, " ")}</td>
                <td className="px-4 py-2.5"><div className="text-navy">{t.label}</div><div className="text-[10px] text-slate-400 font-mono">{t.credential_type}</div></td>
                <td className="px-4 py-2.5">
                  <button onClick={() => save({ template_id: t.template_id, required: !t.required })} data-testid={`cred-tpl-required-${t.template_id}`}
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${t.required ? "bg-navy text-white" : "bg-slate-100 text-slate-500"}`}>{t.required ? "Required" : "Optional"}</button>
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{t.expires ? "Yes" : "No"}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{t.blocks ? t.blocks.replace(/_/g, " ") : "—"}</td>
                <td className="px-4 py-2.5 text-[10px] text-slate-400">{(t.reminder_days || []).join(", ")}d</td>
                <td className="px-4 py-2.5">
                  <button onClick={() => save({ template_id: t.template_id, active: !t.active })} data-testid={`cred-tpl-active-${t.template_id}`}
                    className={`w-10 h-5 rounded-full relative transition-colors ${t.active ? "bg-emerald-500" : "bg-slate-300"}`}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${t.active ? "translate-x-5" : "translate-x-0.5"}`} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AuditLog = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/credentials/audit?limit=200").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (rows === null) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  if (rows.length === 0) return <div className="py-10 text-center text-sm text-slate-400">No credential activity yet.</div>;
  return (
    <div className="bg-white rounded-3xl border border-slate-100 divide-y divide-slate-50" data-testid="cred-admin-audit">
      {rows.map((a) => (
        <div key={a.audit_id} className="px-4 py-2.5 text-xs flex flex-wrap justify-between gap-2">
          <div><span className="font-semibold text-navy">{a.event}</span>
            <span className="text-slate-400 ml-2">{(a.role || "").replace(/_/g, " ")}{a.actor_name ? ` · by ${a.actor_name}` : ""}</span></div>
          <span className="text-slate-400">{new Date(a.at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

export default function AdminCredentials() {
  const [sub, setSub] = useState("overview");
  return (
    <div data-testid="admin-credentials">
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <ShieldCheck className="w-5 h-5 text-cobalt" />
        <h2 className="font-heading text-xl font-bold text-navy mr-3">Credential Intelligence</h2>
        {[["overview", "Overview"], ["credentials", "Credentials"], ["templates", "Requirements"], ["audit", "Audit Log"]].map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} data-testid={`cred-sub-${k}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${sub === k ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
      {sub === "overview" && <Overview />}
      {sub === "credentials" && <CredentialList />}
      {sub === "templates" && <Templates />}
      {sub === "audit" && <AuditLog />}
    </div>
  );
}
